import { Scroller, Slider, Image, Video, Overlay } from '@cian/slider';
import * as React from 'react';

import { Booked } from './components/Booked';
import { CianLayout } from './components/CianLayout';
import { PaginationDots } from './components/PaginationDots';
import { Pessimization } from './components/Pessimization';
import { PlayIcon } from './components/PlayIcon';
import { Stub } from './components/Stub';
import { EMediaType, IMedia, IPessimization } from '../../types';

interface ICarouselProps {
  bookedFromDeveloper?: boolean;
  media: IMedia[];
  pessimization?: IPessimization;
  slotSliderEnd?: React.ReactNode;
  onSlideChange?(index: number): void;
}

export const Carousel: React.FC<ICarouselProps> = ({
  bookedFromDeveloper,
  media: mediaRaw,
  pessimization,
  slotSliderEnd,
  onSlideChange,
}) => {
  const media = React.useMemo(() => {
    const media = mediaRaw;

    if (pessimization && media[0]) {
      media.unshift(media[0]);
    }

    return media;
  }, [mediaRaw, pessimization]);

  if (media.length === 0) {
    return <Stub pessimization={pessimization} slotEnd={slotSliderEnd} />;
  }

  return (
    <Slider onSlideChange={onSlideChange}>
      <Scroller>
        {media.map((m, index) => {
          const key = `slide_${index}`;
          let slide: React.ReactNode = null;

          switch (m.type) {
            case EMediaType.Layout:
            case EMediaType.Photo:
              slide = <Image key={key} src={m.src} objectFit={m.type === EMediaType.Layout ? 'contain' : undefined} />;
              break;
            case EMediaType.Video:
              slide = <Video key={key} src={m.src} previewUrl={m.previewUrl} playButton={<PlayIcon />} />;
              break;
            case EMediaType.CianLayout:
              slide = <CianLayout key={key} src={m.src} />;
              break;
          }

          if (index === 0) {
            if (pessimization) {
              return (
                <Pessimization key={key} pessimization={pessimization}>
                  {slide}
                </Pessimization>
              );
            }

            if (bookedFromDeveloper) {
              return <Booked key={key}>{slide}</Booked>;
            }
          }

          return slide;
        })}
      </Scroller>
      <Overlay hideable={false}>
        <PaginationDots />
      </Overlay>
      {slotSliderEnd}
    </Slider>
  );
};
